import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { INVESTMENT_TYPE_COLORS } from '../config/filters';
import { STATUS_CONFIG } from '../config/statusConfig';
import { API_URL } from '../config/api';
import ImageCarousel from '../components/ImageCarousel';
import PropertyDetailsContent from '../components/PropertyDetailsContent';
import useSaveProperty from '../hooks/useSaveProperty';
import { Heart } from 'lucide-react';
import { trackPropertyEvent, trackConversionEvent } from '../utils/analytics';
import './PropertyDetailsPage.css';



const PropertyDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { saved, saving, toggleSave } = useSaveProperty(id);

    // Track property view when component mounts
    useEffect(() => {
        trackConversionEvent('property_view', 'search');
    }, []);

    const { data: property, isLoading, error } = useQuery({
        queryKey: ['property', id],
        queryFn: async () => {
            const response = await fetch(`${API_URL}/properties/${id}`);
            if (!response.ok) {
                if (response.status === 404) throw new Error('Property not found');
                throw new Error('Failed to fetch property details');
            }
            return response.json();
        },
        retry: 1,
    });

    // Track property view when data is loaded
    useEffect(() => {
        if (property) {
            trackPropertyEvent('view', property.id, {
                price: property.price,
                location: property.address,
                type: property.property_type,
            });
        }
    }, [property]);

    // Track save/unsave actions
    const handleToggleSave = () => {
        const action = saved ? 'unsave' : 'save';
        
        // Track the action before executing
        trackPropertyEvent(action, property?.id, {
            price: property?.price,
            location: property?.address,
            type: property?.property_type,
        });
        
        // Execute the save/unsave action
        toggleSave();
    };

    if (isLoading) {
        return (
            <Layout scrollable={true}>
                <div className="loading-container">
                    <p>Loading property details...</p>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout scrollable={true}>
                <div className="error-container">
                    <h2 className="section-title">Property Not Found</h2>
                    <p className="verdict-text">{error.message}</p>
                    <button type="button" className="btn-primary" style={{ display: 'inline-block', maxWidth: '200px', cursor: 'pointer' }} onClick={() => navigate(-1)}>Back to Listings</button>
                </div>
            </Layout>
        );
    }

    if (!property) return null;

    // Use photos array if available, otherwise fallback to primary_photo_url
    const images = property.photos && property.photos.length > 0
        ? property.photos
        : (property.primary_photo_url ? [property.primary_photo_url] : []);

    const status = property.listing_status || 'FOR_SALE';
    const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.UNKNOWN;

    return (
        <Layout scrollable={true}>
            <div className="property-details-container">

                {/* Hero Image */}
                <div className="hero-image-section">
                    <ImageCarousel images={images} alt={property.address} variant="blur" />

                    {/* Status Chip */}
                    <div
                        className="hero-status-chip"
                        style={{ backgroundColor: statusConfig.bg, color: statusConfig.color, borderColor: statusConfig.color }}
                    >
                        {statusConfig.label}
                    </div>

                    {property.investment_types && property.investment_types.length > 0 && (
                        <div className="hero-tags-overlay">
                            {property.investment_types.map((type, idx) => (
                                <span
                                    key={idx}
                                    className="hero-investment-tag"
                                    style={{ backgroundColor: INVESTMENT_TYPE_COLORS[type] || '#6b7280' }}
                                >
                                    {type}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Heart / Save Button */}
                    <button
                        className={`hero-save-btn ${saved ? 'saved' : ''}`}
                        onClick={handleToggleSave}
                        aria-label={saved ? 'Unsave property' : 'Save property'}
                        disabled={saving}
                    >
                        <Heart
                            size={26}
                            fill={saved ? '#ef4444' : 'none'}
                            stroke={saved ? '#ef4444' : 'white'}
                            strokeWidth={2}
                        />
                    </button>
                </div>

                {/* Back Link & Shared Content */}
                <div className="details-content-wrapper" style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px 16px' }}>
                    <div className="details-top-row">
                        <button type="button" className="back-link" onClick={() => navigate(-1)}>← Back</button>
                    </div>

                    <PropertyDetailsContent property={property} />
                </div>

            </div>
        </Layout>
    );
};

export default PropertyDetailsPage;
